import React, { useState } from 'react';
import { useUniqueWcagValues } from 'common-react';
import clsx from 'clsx';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@material-ui/core';
import { CollapseButton } from '../CollapseButton/CollapseButton';
import { useAccordionWcag } from './tools/useAccordionWcag';

import useStyles from './Accordion.style';

export enum AriaHeadingLevel {
  H2 = 2,
  H3 = 3,
  H4 = 4,
  H5 = 5,
  H6 = 6
}

export interface AccordionProps {
  title?: string | React.ReactNode;
  renderTitle?: (isExpanded: boolean) => string | React.ReactNode;
  variant?: 'heading' | 'toggle';
  ariaLevel?: AriaHeadingLevel;
  autoTestId?: string;
  disableInteractionStylesFromCollapseButton?: boolean;
}

export interface AccordionStyleProps {
  transparent?: boolean;
  className?: string;
  summaryClassName?: string;
  detailsClassName?: string;
  titleClassName?: string;
  type?: 'inline' | 'primary';
  expanded?: boolean;
  expansionDisabled?: boolean;
  isAccordionExpanded?: boolean;
  shouldRemoveWcagAttributesFromAccordionSummary?: boolean;
  onChange?: (isExpanded: boolean) => void;
  focusVisible?: boolean;
  expandElement?: React.ReactNode;
  position?: 'only' | 'last' | 'notLast';
}

export const getAccordionPosition = (
  index: number,
  length: number
): 'only' | 'last' | 'notLast' => {
  if (length === 1) return 'only';
  if (index === length - 1) return 'last';
  return 'notLast';
};

const CustomAccordion: React.FC<AccordionProps & AccordionStyleProps> = ({
  children,
  title,
  transparent = false,
  type = 'inline',
  className,
  summaryClassName,
  detailsClassName,
  titleClassName,
  expanded,
  expandElement,
  expansionDisabled,
  onChange = () => {},
  renderTitle,
  ariaLevel,
  variant='toggle',
  focusVisible = true,
  shouldRemoveWcagAttributesFromAccordionSummary = false,
  position = 'only',
  autoTestId,
  disableInteractionStylesFromCollapseButton = false,
}) => {
  const [isAccordionExpanded, setIsAccordionExpanded] = useState(
    expanded || false
  );
  const c = useStyles({ transparent, type, expansionDisabled, focusVisible, isAccordionExpanded, position });
  const headingAttributes = variant === 'heading' ? { role: 'heading', 'aria-level': ariaLevel } : {};
  const { getUniqueValue } = useUniqueWcagValues();
  const panelHeaderId = getUniqueValue('panel-header');
  const panelContentId = getUniqueValue('panel-content');
  const { resetWcagAttributes } = useAccordionWcag();
  const manageWcagAttributesForAccordionSummary = shouldRemoveWcagAttributesFromAccordionSummary ? resetWcagAttributes : {};
  const accordionTitle = renderTitle ? renderTitle(isAccordionExpanded) : title;

  return (
    <Accordion
      className={clsx(c.root, className)}
      expanded={expansionDisabled ? false : expanded}
      onChange={(_, isExpanded) => {
        setIsAccordionExpanded(isExpanded);
        !expansionDisabled && onChange(isExpanded);
      }}
      square={true}
    >
      <AccordionSummary
        expandIcon={expandElement || undefined}
        data-testid="accordion-summary"
        data-autotestid={autoTestId}
        id={panelHeaderId}
        aria-controls={panelContentId}
        tabIndex={focusVisible ? 0 : -1}
        className={summaryClassName}
        {...manageWcagAttributesForAccordionSummary}
      >
        {expandElement ? (
          <span
            className={clsx(c.title, expanded ? 'active' : null, titleClassName)}
            {...headingAttributes}
          >
            {accordionTitle}
          </span>
        ) : (
          <CollapseButton
            as="span"
            className={clsx(c.title, expanded ? 'active' : null, titleClassName)}
            expanded={isAccordionExpanded}
            disableInteractionStyles={disableInteractionStylesFromCollapseButton}
            {...headingAttributes}
          >
            {accordionTitle}
          </CollapseButton>
        )}
      </AccordionSummary>
      <AccordionDetails className={detailsClassName || c.details}>
          {children}
      </AccordionDetails>
    </Accordion>
  );
};

export { CustomAccordion as Accordion };
